export interface PaxInfoParams {
  searchSettings?: Content.Components['SearchSettingsDto'];
  t: (key: string) => string;
}

/**
 * Builds a human-readable passenger summary string (adults and children).
 *
 * The function:
 * - Reads adultsNo and childrenNo from searchSettings.selectedPaxDistribution (defaults to 0).
 * - Chooses singular or plural translation keys for adults/children based on the count.
 * - Lower-cases the translated words for adults/children and the localized conjunction ("and").
 * - Prefixes the result with the localized "results are for" phrase.
 *
 * @param searchSettings - The search settings DTO that contains selectedPaxDistribution with adultsNo and childrenNo.
 * @param t - Translation function that accepts a key and returns the localized string for that key.
 * @returns A localized, human-readable string describing the number of adults and children for the search results.
 */

export const getPaxInfo = ({ searchSettings, t }: PaxInfoParams): string => {
  if (!searchSettings) return '';

  const adultNumber = searchSettings.selectedPaxDistribution?.adultsNo || 0;
  const childrenNumber =
    searchSettings.selectedPaxDistribution?.childrenNo || 0;
  const resultsForAdults =
    adultNumber > 0
      ? adultNumber > 1
        ? `${adultNumber} ${t('items.common.words.adults').toLowerCase()}`
        : `${adultNumber} ${t('items.common.words.adult').toLowerCase()}`
      : '';
  const resultsForChildren =
    childrenNumber > 0
      ? childrenNumber > 1
        ? `${childrenNumber} ${t('items.common.words.children').toLowerCase()}`
        : `${childrenNumber} ${t('items.common.words.child').toLowerCase()}`
      : '';

  let paxInfo = `${resultsForAdults}`;

  if (resultsForChildren) {
    paxInfo += ` ${t('items.common.words.and').toLowerCase()} ${resultsForChildren}`;
  }

  return paxInfo;
};
